import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs, addDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getMockDish, getMockRatings } from '../services/mockData';
import { safeFirebaseQuery } from '../utils/firebaseHelper';
import RatingForm from '../components/RatingForm';
import RatingCard from '../components/RatingCard';
import PhotoUpload from '../components/PhotoUpload';
import SocialShare from '../components/SocialShare';
import { DishDetailSEO } from '../components/PageSEO';
import './DishDetail.css';

function DishDetail({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [dish, setDish] = useState(null);
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userHasRated, setUserHasRated] = useState(false);
  const [voteCount, setVoteCount] = useState(0);
  const [userHasVoted, setUserHasVoted] = useState(false);
  const [isVoting, setIsVoting] = useState(false);
  const [userPhotos, setUserPhotos] = useState([]);
  const [isFavorite, setIsFavorite] = useState(false);

  const checkUserVote = useCallback(async () => {
    if (!user) {
      setUserHasVoted(false);
      return;
    }

    try {
      const votesQuery = query(
        collection(db, 'votes'),
        where('dishId', '==', id),
        where('userId', '==', user.uid)
      );
      const votesSnapshot = await getDocs(votesQuery);
      setUserHasVoted(!votesSnapshot.empty);
    } catch (error) {
      console.error('Error checking vote:', error);
    }
  }, [user, id]);

  const checkUserFavorite = useCallback(async () => {
    if (!user) {
      setIsFavorite(false);
      return;
    }

    try {
      const favoriteQuery = query(
        collection(db, 'favorites'),
        where('dishId', '==', id),
        where('userId', '==', user.uid)
      );
      const favoriteSnapshot = await getDocs(favoriteQuery);
      setIsFavorite(!favoriteSnapshot.empty);
    } catch (error) {
      console.error('Error checking favorite:', error);
    }
  }, [user, id]);

  const fetchUserPhotos = useCallback(async () => {
    try {
      const photosQuery = query(
        collection(db, 'dishPhotos'),
        where('dishId', '==', id),
        where('approved', '==', true)
      );
      const photosSnapshot = await getDocs(photosQuery);
      const photosData = photosSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUserPhotos(photosData);
    } catch (error) {
      console.error('Error fetching user photos:', error);
    }
  }, [id]);

  const handleToggleFavorite = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      const favoriteQuery = query(
        collection(db, 'favorites'),
        where('dishId', '==', id),
        where('userId', '==', user.uid)
      );
      const favoriteSnapshot = await getDocs(favoriteQuery);

      if (!favoriteSnapshot.empty) {
        await deleteDoc(favoriteSnapshot.docs[0].ref);
        setIsFavorite(false);
      } else {
        await addDoc(collection(db, 'favorites'), {
          userId: user.uid,
          dishId: id,
          createdAt: new Date()
        });
        setIsFavorite(true);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const handlePhotoUploadComplete = () => {
    fetchUserPhotos();
  };

  const fetchDish = useCallback(async () => {
    try {
      const dishData = await safeFirebaseQuery(
        async () => {
          const docRef = doc(db, 'dishes', id);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            return { id: docSnap.id, ...docSnap.data() };
          }
          return null;
        },
        async () => {
          return await getMockDish(id);
        },
        'Error fetching dish'
      );

      if (dishData) {
        setDish(dishData);
        setVoteCount(dishData.voteCount || 0);
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Error fetching dish:', error);
      // Try mock data as fallback
      try {
        const mockDish = await getMockDish(id);
        if (mockDish) {
          setDish(mockDish);
          setVoteCount(mockDish.voteCount || 0);
        } else {
          navigate('/');
        }
      } catch {
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchRatings = useCallback(async () => {
    try {
      const ratingsData = await safeFirebaseQuery(
        async () => {
          const q = query(collection(db, 'ratings'), where('dishId', '==', id));
          const querySnapshot = await getDocs(q);
          return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
        },
        async () => {
          return await getMockRatings(id);
        },
        'Error fetching ratings'
      );

      // Sort by most recent first
      ratingsData.sort((a, b) => {
        const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : new Date(a.createdAt).getTime();
        const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : new Date(b.createdAt).getTime();
        return bTime - aTime;
      });
      setRatings(ratingsData);

      // Check if current user has already rated
      if (user) {
        const userRating = ratingsData.find(r => r.userId === user.uid);
        setUserHasRated(!!userRating);
      }
    } catch (error) {
      console.error('Error fetching ratings:', error);
      // Use mock data as fallback
      try {
        const mockRatingsData = await getMockRatings(id);
        setRatings(mockRatingsData);
      } catch {
        setRatings([]);
      }
    }
  }, [id, user]);

  useEffect(() => {
    if (id) {
      checkUserVote();
      checkUserFavorite();
      fetchDish();
      fetchRatings();
      fetchUserPhotos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user]);

  const handleVote = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (userHasVoted) {
      return;
    }

    setIsVoting(true);
    try {
      // Add vote
      await addDoc(collection(db, 'votes'), {
        dishId: id,
        userId: user.uid,
        createdAt: new Date()
      });

      // Update dish vote count
      const newVoteCount = voteCount + 1;
      await updateDoc(doc(db, 'dishes', id), {
        voteCount: newVoteCount
      });

      setVoteCount(newVoteCount);
      setUserHasVoted(true);
      fetchDish();
    } catch (error) {
      console.error('Error voting:', error);
      alert('Error voting. Please try again.');
    } finally {
      setIsVoting(false);
    }
  };

  const handleRatingSubmit = async (ratingData) => {
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      // Add rating
      await addDoc(collection(db, 'ratings'), {
        dishId: id,
        userId: user.uid,
        userEmail: user.email,
        foodRating: ratingData.rating,
        comment: ratingData.comment || null,
        createdAt: new Date()
      });

      // Update dish average rating
      const newRatings = [...ratings, { foodRating: ratingData.rating }];
      const totalRatings = newRatings.length;
      const sumRatings = newRatings.reduce((sum, r) => sum + r.foodRating, 0);
      const averageRating = sumRatings / totalRatings;

      await updateDoc(doc(db, 'dishes', id), {
        averageRating: averageRating,
        totalRatings: totalRatings
      });

      // Refresh data
      fetchDish();
      fetchRatings();
    } catch (error) {
      console.error('Error submitting rating:', error);
    }
  };

  const renderStars = (rating) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const stars = [];

    for (let i = 0; i < fullStars; i++) {
      stars.push(<span key={i} className="star full">★</span>);
    }
    if (hasHalfStar) {
      stars.push(<span key="half" className="star half">★</span>);
    }
    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<span key={`empty-${i}`} className="star empty">★</span>);
    }

    return stars;
  };

  if (loading) {
    return (
      <div className="dish-detail-container">
        <div className="loading">Loading dish...</div>
      </div>
    );
  }

  if (!dish) {
    return null;
  }

  return (
    <>
      {dish && <DishDetailSEO dish={dish} />}
      <div className="dish-detail-container">
        <button onClick={() => navigate('/')} className="btn-back">
          ← Back to Dishes
        </button>

        <div className="dish-detail-header">
          <div className="dish-image-section">
            {dish.imageUrl ? (
              <img src={dish.imageUrl} alt={dish.name} className="dish-detail-image" />
            ) : (
              <div className="dish-detail-image-placeholder">
                <span className="placeholder-icon-large">🍽️</span>
              </div>
            )}
          </div>

          <div className="dish-info-section">
            <div className="dish-title-section">
              <h1>{dish.name}</h1>
              <div className="dish-badges">
                {dish.dishType && (
                  <span className="dish-type-badge-large">{dish.dishType}</span>
                )}
                {dish.cuisine && (
                  <span className="cuisine-badge-large">{dish.cuisine}</span>
                )}
              </div>
            </div>

            {dish.restaurantName && (
              <div className="dish-restaurant-info">
                <p className="restaurant-name">📍 {dish.restaurantName}</p>
                {dish.location && (
                  <p className="restaurant-location">{dish.location}</p>
                )}
              </div>
            )}

            {dish.description && (
              <div className="dish-description-section">
                <h3>Why This Dish is Special</h3>
                <p className="dish-description-text">{dish.description}</p>
              </div>
            )}

            <div className="rating-summary">
              <div className="stars-large">
                {renderStars(dish.averageRating || 0)}
              </div>
              <div className="rating-info-large">
                <span className="rating-value-large">
                  {dish.averageRating ? dish.averageRating.toFixed(1) : '0.0'}
                </span>
                <span className="rating-count-large">
                  ({dish.totalRatings || 0} {dish.totalRatings === 1 ? 'rating' : 'ratings'})
                </span>
              </div>
              <p className="rating-label-large">Food Quality Rating</p>

              <div className="vote-section-detail">
                <button
                  onClick={handleVote}
                  disabled={!user || userHasVoted || isVoting}
                  className={`vote-button-large ${userHasVoted ? 'voted' : ''}`}
                  title={userHasVoted ? 'You already voted' : user ? 'Vote for this dish' : 'Login to vote'}
                >
                  <span className="vote-icon">👍</span>
                  <span className="vote-text">{userHasVoted ? 'Voted!' : 'Vote'}</span>
                  <span className="vote-count">{voteCount}</span>
                </button>

                {user && (
                  <button
                    onClick={handleToggleFavorite}
                    className={`favorite-button-large ${isFavorite ? 'favorited' : ''}`}
                    title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                  >
                    <span className="favorite-icon-large">{isFavorite ? '❤️' : '🤍'}</span>
                    <span>{isFavorite ? 'Saved' : 'Save'}</span>
                  </button>
                )}

                <SocialShare dish={dish} />
              </div>
            </div>
          </div>
        </div>

        {user && !userHasRated && (
          <div className="rating-section">
            <h2>Rate This Dish</h2>
            <p className="rating-instruction">
              Rate this dish based on food quality only. Consider taste, quality of ingredients,
              preparation, and presentation. Do not factor in service, ambiance, or price.
            </p>
            <RatingForm onSubmit={handleRatingSubmit} />
          </div>
        )}

        {user && userHasRated && (
          <div className="already-rated">
            <p>✓ You have already rated this dish's food quality.</p>
          </div>
        )}

        {!user && (
          <div className="login-prompt">
            <p>Please <a href="/login">login</a> or <a href="/signup">sign up</a> to rate this dish's food quality.</p>
          </div>
        )}

        {user && (
          <div className="photo-upload-section">
            <PhotoUpload
              dishId={id}
              userId={user.uid}
              onUploadComplete={handlePhotoUploadComplete}
            />
          </div>
        )}

        {userPhotos.length > 0 && (
          <div className="user-photos-section">
            <h2>Community Photos ({userPhotos.length})</h2>
            <div className="user-photos-grid">
              {userPhotos.map(photo => (
                <div key={photo.id} className="user-photo-item">
                  <img src={photo.imageUrl} alt={`${dish.name} user upload`} />
                  <p className="photo-date">
                    {new Date(photo.createdAt?.toMillis()).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="ratings-list">
          <h2>Food Quality Ratings ({ratings.length})</h2>
          {ratings.length === 0 ? (
            <p className="no-ratings">No ratings yet. Be the first to rate!</p>
          ) : (
            <div className="ratings-grid">
              {ratings.map(rating => (
                <RatingCard key={rating.id} rating={rating} />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default DishDetail;

